import { useCallback, useEffect, useState } from 'react'
import Icon from '../components/Icon'
import { worx } from './client'
import Tickets from '../components/Tickets'
import VoiceOfGod from '../components/VoiceOfGod'

type Tab = 'queue' | 'reports' | 'tickets' | 'migrations'

/**
 * The application's own admin console, rendered BESIDE core's.
 *
 * components/Admin.tsx says it plainly: an application adds its own tab
 * alongside core's, it does not modify them, because a concept added there is
 * inherited by every other Studio42 project. Employer verification is not a
 * thing weedweb or Ferry has, so it lives here.
 *
 * ── Two of these were already built and never mounted ───────────────────────
 *
 * `Tickets` and `VoiceOfGod` shipped in the template as components with no
 * screen to live on — the same shape as the missing /api/tickets routes that
 * were found in September. A piece that exists in the file listing and nowhere
 * in the app is indistinguishable from a piece that was never written, so they
 * are wired here rather than left to be rediscovered.
 */
export default function WorxAdmin({
  capabilities, commandLine = 'bottom',
}: {
  capabilities: string[]
  /**
   * Where the command line sits. 'bottom' is the original, under the ticket
   * queue it feeds. The account screen passes 'top': on a phone, below four
   * tabs of tables, "bottom" is a long scroll and people reported they could
   * not find it.
   */
  commandLine?: 'top' | 'bottom'
}) {
  const [tab, setTab] = useState<Tab>('queue')
  /*
   * Bumped after every command, and used as the active tab's `key`.
   *
   * `verify Duffy's` at the command line changes exactly what the queue above
   * it is displaying, and a console that still shows the old row after the
   * reply says it worked is the reason nobody trusts the command line. A
   * remount is blunt but it cannot miss a case, which a hand-written list of
   * 'which tabs does which verb affect' certainly would.
   */
  const [nonce, setNonce] = useState(0)

  const TABS: { id: Tab; label: string; capability: string }[] = [
    { id: 'queue', label: 'Employers', capability: 'users.read' },
    { id: 'reports', label: 'Reports', capability: 'users.read' },
    { id: 'tickets', label: 'Tickets', capability: 'operations.write' },
    { id: 'migrations', label: 'Migrations', capability: 'errors.read' },
  ]

  const visible = TABS.filter((x) => capabilities.includes(x.capability))
  if (!visible.length) return null

  return (
    <section className="card">
      <h2>Vieques Worx</h2>

      {commandLine === 'top' && <VoiceOfGod onChanged={() => setNonce((n) => n + 1)} />}

      <nav className="tabs">
        {visible.map((x) => (
          <button key={x.id} onClick={() => setTab(x.id)} aria-current={tab === x.id}>{x.label}</button>
        ))}
      </nav>

      {tab === 'queue' && <EmployerQueue key={nonce} />}
      {tab === 'reports' && <Reports key={nonce} />}
      {tab === 'tickets' && <Tickets key={nonce} />}
      {tab === 'migrations' && <Migrations key={nonce} />}

      {/*
        The command line, always at the bottom and always available to anyone
        who can see this console. It is the "low-level developer": it does the
        safe obvious things and raises a ticket for everything else — which is
        why it sits under the ticket queue it feeds.
      */}
      {commandLine === 'bottom' && <VoiceOfGod onChanged={() => setNonce((n) => n + 1)} />}
    </section>
  )
}

/* ── Employer verification ────────────────────────────────────────────────── */

function EmployerQueue() {
  const [status, setStatus] = useState('pending')
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const [reason, setReason] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setRows((await worx.employerQueue(status)).employers)
    } catch {
      setRows([])
    }
  }, [status])

  useEffect(() => { void load() }, [load])

  async function decide(id: string, next: string) {
    setBusy(id)
    setError(null)
    try {
      await worx.decideEmployer(id, next, reason[id])
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <nav className="tabs">
        {['pending', 'verified', 'rejected', 'suspended', 'all'].map((s) => (
          <button key={s} onClick={() => setStatus(s)} aria-current={status === s}>{s}</button>
        ))}
      </nav>

      {error && <p className="error">{error}</p>}
      {!rows.length && <p className="muted">Nothing here.</p>}

      <ul className="list w-queue">
        {rows.map((e) => {
          const id = String(e.id)
          return (
            <li key={id}>
              <div className="w-queue-row">
                <div>
                  <strong>{String(e.name)}</strong>
                  <div className="muted small">
                    {String(e.contact_name ?? '—')} · {String(e.phone ?? '')} {String(e.email ?? '')}
                  </div>
                  {/* The evidence they gave, in full and unedited. It is the
                      entire basis of the decision this screen exists to make. */}
                  {Boolean(e.proof) && <p className="w-prose w-proof">{String(e.proof)}</p>}
                  <div className="muted small">{String(e.jobs)} posting(s)</div>
                  {Boolean(e.decision) && <div className="muted small">Last decision: {String(e.decision)}</div>}
                </div>
                <span className={`w-pill w-pill-${String(e.status)}`}>{String(e.status)}</span>
              </div>

              <div className="w-card-actions">
                <input
                  value={reason[id] ?? ''}
                  onChange={(ev) => setReason((r) => ({ ...r, [id]: ev.target.value }))}
                  placeholder="Reason — required to reject or suspend"
                  aria-label="Reason"
                />
                {e.status !== 'verified' && (
                  <button className="primary" disabled={busy === id} onClick={() => void decide(id, 'verified')}>
                    Verify
                  </button>
                )}
                {e.status !== 'rejected' && (
                  <button disabled={busy === id} onClick={() => void decide(id, 'rejected')}>Reject</button>
                )}
                {e.status !== 'suspended' && (
                  <button disabled={busy === id} onClick={() => void decide(id, 'suspended')}>Suspend</button>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </>
  )
}

/* ── Reports ──────────────────────────────────────────────────────────────── */

function Reports() {
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setRows((await worx.reports('open')).reports)
    } catch {
      setRows([])
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function resolve(id: string, status: string) {
    setBusy(id)
    try {
      await worx.resolveReport(id, status)
      await load()
    } finally {
      setBusy(null)
    }
  }

  if (!rows.length) return <p className="muted">No open reports.</p>

  return (
    <ul className="list">
      {rows.map((r) => {
        const id = String(r.id)
        return (
          <li key={id}>
            <div>
              <strong>{String(r.reason)}</strong> — {String(r.subject_type)}
              <div className="muted small">
                {String(r.reporterName ?? 'someone')} · {new Date(Number(r.created_at)).toLocaleString()}
              </div>
              {Boolean(r.detail) && <p className="w-prose">{String(r.detail)}</p>}
            </div>
            <div className="w-card-actions">
              <button disabled={busy === id} onClick={() => void resolve(id, 'actioned')}>Actioned</button>
              <button disabled={busy === id} onClick={() => void resolve(id, 'dismissed')}>Dismiss</button>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

/* ── Migrations ───────────────────────────────────────────────────────────── */

/**
 * The migration runner, Tracker42's page on D1.
 *
 * It answers "did it run" without a terminal, and — unlike wrangler, which
 * matches on filename alone — it can say that a file was EDITED after it was
 * applied. See services/api/src/worx/migrations.ts.
 *
 * Applying asks for the filename to be typed. That is not friction for its own
 * sake: this is the one screen in the app where a mis-click is not undone by
 * clicking again, and the environment is named beside the button because the
 * same page served from beta and production looks identical.
 */
function Migrations() {
  const [state, setState] = useState<Awaited<ReturnType<typeof worx.migrations>> | null>(null)
  const [confirm, setConfirm] = useState('')
  const [target, setTarget] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setState(await worx.migrations())
    } catch {
      setState(null)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function apply(name: string, markOnly: boolean) {
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      const r = await worx.applyMigration(name, confirm, markOnly)
      setMessage(markOnly ? `${name} marked as applied.` : `${name}: ${r.statements} statement(s) ran.`)
      setTarget(null)
      setConfirm('')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That failed.')
    } finally {
      setBusy(false)
    }
  }

  if (!state) return <p className="muted">…</p>

  return (
    <>
      <p className="muted small">
        <strong>{state.environment}</strong> · {state.pending} pending
        {state.orphans > 0 && ` · ${state.orphans} orphan(s)`}
      </p>

      {message && <p className="w-ok">{message}</p>}
      {error && <p className="error">{error}</p>}

      <ul className="list w-migrations">
        {state.migrations.map((m) => (
          <li key={m.name} className={`w-mig w-mig-${m.state}`}>
            <div>
              <strong>{m.name}</strong>
              <div className="muted small">
                {m.state === 'applied' && `applied ${m.appliedAt}`}
                {m.state === 'pending' && `${m.statements} statement(s), never applied`}
                {/* An orphan is a row whose file is gone — this Worker is
                    older than the database it is talking to, or a migration
                    was deleted. Neither is fixable from here, and saying so
                    is more useful than a button that cannot help. */}
                {m.state === 'orphan' && `in the database, no file — nothing to run`}
              </div>
            </div>

            {m.state === 'pending' && state.canApply && (
              target === m.name ? (
                <div className="w-card-actions">
                  <input
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder={m.name}
                    aria-label="Type the filename to confirm"
                  />
                  <button className="primary" disabled={busy || confirm !== m.name} onClick={() => void apply(m.name, false)}>
                    Run on {state.environment}
                  </button>
                  <button disabled={busy || confirm !== m.name} onClick={() => void apply(m.name, true)}>
                    Mark applied
                  </button>
                  <button className="link" onClick={() => { setTarget(null); setConfirm('') }}>Cancel</button>
                </div>
              ) : (
                <button onClick={() => { setTarget(m.name); setConfirm('') }}>Apply…</button>
              )
            )}
            {m.state === 'applied' && (
              <span className="w-pill w-pill-verified"><Icon name="check" size={14} title="applied" /></span>
            )}
          </li>
        ))}
      </ul>
    </>
  )
}
